import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Popover,
  TextField,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { LuPencil } from 'react-icons/lu';
import { ICandidate, IJob, getCandidateSubmissionSettingsTemplate } from 'wisestep-api';
import {
  WsDialog,
  WsTypoVariants,
  WsTypoColors,
  WsTypography,
  WsButton,
  SendEmailIcon,
  WsColors,
  WsDialogSize,
} from 'wisestep-base';

import { replacePlaceHoldersEmailBody } from '../../../components/BulkEmail/helper/apiCalls';
import { useWorkspaceContext } from '../../../context';

import EmailPreview from './EmailPreview';
import InternalSubmission from './InternalSubmission';

interface SubmitOptionsProps {
  onInternalSubmit: (recruiters: any, comment: string, emailBody: string) => void;
  onExternalSubmit: (proceed: boolean, emailBody: string) => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  getSubmissionPayload?: () => ICandidate | null;
  onSubmissionRejection?: (comment: string) => void;
  job?: IJob | null;
  submissionMailBody?: string | null;
}

const SubmitOptions = ({
  onInternalSubmit,
  onExternalSubmit,
  anchorEl,
  onClose,
  getSubmissionPayload,
  onSubmissionRejection,
  job,
  submissionMailBody,
}: SubmitOptionsProps) => {
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [submissionData, setSubmissionData] = useState<ICandidate | null>(null);
  const [rejectDialogOpen, setRejectionDialogOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [showError, setShowError] = useState(false);
  const [emailBody, setEmailBody] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');
  const [isEmailDataLoaded, setIsEmailDataLoaded] = useState<boolean>(true);
  const { id } = useWorkspaceContext();

  const getEmailData = useCallback(
    async (candidateInfo: ICandidate) => {
      try {
        setIsEmailDataLoaded(false);
        const setting = await getCandidateSubmissionSettingsTemplate(
          job?.submissionFormTemplete ?? '',
          id ?? ''
        );
        const { updatedSubject, updatedBody } = await replacePlaceHoldersEmailBody(
          setting?.data,
          candidateInfo,
          (job as IJob) ?? {},
          false
        );
        setEmailSubject(updatedSubject ?? '');
        setEmailBody(submissionMailBody ?? updatedBody ?? '');
      } catch (error) {
        console.error('Error fetching submission settings:', error);
      } finally {
        setIsEmailDataLoaded(true);
      }
    },
    [job, id, submissionMailBody]
  );

  useEffect(() => {
    submissionData && getEmailData(submissionData);
  }, [submissionData, getEmailData]);

  const onInternalSubmitClick = () => {
    setInternalDialogOpen(true);
    getSubmissionPayload && setSubmissionData(getSubmissionPayload());
    onClose();
  };

  const onExternalSubmitClick = () => {
    getSubmissionPayload && setSubmissionData(getSubmissionPayload());
    setExternalDialogOpen(true);
    onClose();
  };

  const handleInternalSubmit = (recruiters: any, comment: string) => {
    if (isEmpty(recruiters) || isEmpty(emailBody)) return;
    onInternalSubmit(recruiters, comment, emailBody);
    setInternalDialogOpen(false);
  };

  const handleExternalSubmit = (proceed: boolean, emailData: string) => {
    onExternalSubmit(proceed, emailData);
    setExternalDialogOpen(false);
  };

  const handleReject = () => {
    if (isEmpty(comment)) {
      setShowError(true);
      return;
    }
    onSubmissionRejection && onSubmissionRejection(comment);
    setShowError(false);
    setRejectionDialogOpen(false);
  };

  const onRejectionClick = () => {
    setRejectionDialogOpen(true);
    onClose();
  };

  const onRejectionDialogClose = () => {
    setRejectionDialogOpen(false);
    setShowError(false);
  };

  const isPopover = Boolean(anchorEl);

  const handleSetEmailBody = (body: string) => setEmailBody(body ?? '');

  return (
    <>
      {isPopover && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box p={2} display="flex" flexDirection="column" gap={1}>
            <WsButton variant="text" startIcon={<LuPencil />} onClick={onInternalSubmitClick}>
              Submit Internal
            </WsButton>
            <WsButton variant="text" startIcon={<SendEmailIcon />} onClick={onExternalSubmitClick}>
              Submit External
            </WsButton>
            {onSubmissionRejection && (
              <WsButton variant="text" startIcon={<BlockIcon />} onClick={onRejectionClick}>
                Reject Internally
              </WsButton>
            )}
          </Box>
        </Popover>
      )}
      <InternalSubmission
        open={internalDialogOpen}
        emailBody={emailBody}
        onClose={() => setInternalDialogOpen(false)}
        onSubmit={handleInternalSubmit}
        emailSubject={emailSubject}
        isEmailDataLoaded={isEmailDataLoaded}
        setEmailBody={handleSetEmailBody}
      />
      <WsDialog openDialog={externalDialogOpen} size={WsDialogSize.MEDIUM}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          bgcolor={WsColors.BorderGrey}
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK} bold>
            Submit Externally
          </WsTypography>
          <IconButton onClick={() => setExternalDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <EmailPreview
            emailBody={emailBody}
            emailSubject={emailSubject}
            isLoaded={isEmailDataLoaded}
            onProceed={(emailData) => handleExternalSubmit(true, emailData ?? '')}
            setEmailBody={handleSetEmailBody}
          />
        </DialogContent>
      </WsDialog>
      <WsDialog openDialog={rejectDialogOpen} size={WsDialogSize.MEDIUM}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ p: 2, bgcolor: WsColors.BorderGrey }}>
            <Grid container alignItems="center">
              <Grid item xs>
                <WsTypography variant={WsTypoVariants.TITLE} bold>
                  Reject Internally
                </WsTypography>
              </Grid>
              <Grid item>
                <IconButton onClick={onRejectionDialogClose} size="small">
                  <CloseIcon sx={{ color: WsColors.GreyBlack }} />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Grid container direction="column" spacing={1.5} mt={2}>
            <Grid item>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK} bold>
                Comment the reason for rejection
              </WsTypography>
            </Grid>
            <Grid item>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                Comment *
              </WsTypography>
            </Grid>
            <Grid item>
              <TextField
                multiline
                fullWidth
                minRows={4}
                variant="outlined"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Type your comment here"
                error={showError}
                helperText={showError ? 'Comment is required' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <WsButton onClick={handleReject}>Reject</WsButton>
        </DialogActions>
      </WsDialog>
    </>
  );
};

export default SubmitOptions;
